import React, { useState, useEffect } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { useSession } from "../../firebase/UserProvider";
import * as db from "../../firebase/tasks";
import { TaskCircle } from "../../components/tms/TaskCircle";
import { LabelMentionInput } from "../../components/tms/LabelMentionInput";
import { TaskToolbar } from "../../components/tms/TaskToolbar";
import { TaskRightPanel } from "../../components/tms/TaskRightPanel";
import { CommentThread } from "../../components/tms/CommentThread";
import { LabelPill } from "../../components/tms/LabelPill";
import { PriorityPicker } from "../../components/tms/PriorityPicker";
import { DatePicker } from "../../components/tms/DatePicker";
import { RecurrencePicker } from "../../components/tms/RecurrencePicker";

const parseInput = (text, sysLabels) => {
  const words = text.trim().split(/\s+/);
  const labels = [];
  let i = 0;
  while (i < words.length && words[i].startsWith("@")) {
    const name = words[i].slice(1);
    const match = sysLabels.find(l => l.name.toLowerCase() === name.toLowerCase());
    if (!match) break;
    if (!labels.includes(match.name)) labels.push(match.name);
    i++;
  }
  return { title: words.slice(i).join(" "), labels };
};

export default function EditTask() {
  const { taskNumber } = useParams();
  const history = useHistory();
  const { user } = useSession();
  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [inputText, setInputText] = useState("");
  const [systemLabels, setSystemLabels] = useState([]);
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [allNums, setAllNums] = useState([]);
  const [err, setErr] = useState(false);

  const getUserName = (uid) => {
    if (!uid) return "Unassigned";
    const matched = users.find((u) => u.uid === uid);
    return matched ? matched.name : "Unknown User";
  };

  useEffect(() => {
    let unsubTask;
    const init = async () => {
      const taskData = await db.getTaskByNumber(taskNumber);
      if (!taskData) return setErr(true);
      setOriginal(taskData);
      setDraft(taskData);
      const prefix = (taskData.labels || []).map(l => `@${l}`).join(" ");
      setInputText(prefix ? `${prefix} ${taskData.title}` : taskData.title);
      const cms = await db.getComments(taskData.id);
      setComments(cms);

      unsubTask = db.subscribeToTask(taskData.id, (updated) => {
        if (!updated) return;
        setDraft(prev => {
          if (!prev) return updated;
          const merged = { ...prev, ...updated, title: prev.title, description: prev.description };
          if (JSON.stringify(updated.labels || []) !== JSON.stringify(prev.labels || [])) {
            const pref = (updated.labels || []).map(l => `@${l}`).join(" ");
            setInputText(pref ? `${pref} ${prev.title}` : prev.title);
          }
          return merged;
        });
      });
    };
    init();
    const unsubLabels = db.subscribeToLabels(setSystemLabels);
    db.listTaskUsers().then(setUsers);
    db.listTasks().then(list => setAllNums(list.map(t => Number(t.taskNumber)).filter(n => !isNaN(n)).sort((a,b)=>a-b)));
    return () => {
      if (unsubTask) unsubTask();
      unsubLabels();
    };
  }, [taskNumber]);

  const handleInputChange = (val) => {
    setInputText(val);
    const parsed = parseInput(val, systemLabels);
    setDraft(prev => ({ ...prev, title: parsed.title, labels: parsed.labels }));
  };

  const changeField = async (field, val, auditComment) => {
    try {
      if (auditComment) {
        const authorName = user?.displayName || user?.email || "System";
        await db.addComment(draft.id, {
          authorUid: user?.uid || "system",
          authorName: authorName,
          isSystem: true,
          ...auditComment,
          text: auditComment.text ? `${auditComment.text} by ${authorName}` : auditComment.text
        });
      }
      await db.updateTask(draft.id, { [field]: val, updatedByUid: user?.uid || "system" });
      setDraft(prev => ({ ...prev, [field]: val }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleStatus = () => {
    const newStatus = draft.status === "Closed" ? "Open" : "Closed";
    changeField("status", newStatus, {
      type: "state_change",
      text: `Status changed from "${draft.status}" to "${newStatus}"`,
      oldValue: draft.status,
      newValue: newStatus
    });
  };

  const handleDateChange = (d) => {
    const val = d || null;
    if (val === draft.dueDate) return;
    const oldV = draft.dueDate || "none";
    const newV = val || "none";
    changeField("dueDate", val, {
      type: "date_change",
      text: `Due date changed from "${oldV}" to "${newV}"`,
      oldValue: draft.dueDate || null,
      newValue: val
    });
  };

  const handlePriorityChange = (p) => {
    if (p === draft.priority) return;
    changeField("priority", p, {
      type: "state_change",
      text: `Priority changed from "${draft.priority}" to "${p}"`,
      oldValue: draft.priority,
      newValue: p
    });
  };

  const handleRecurrenceChange = (newRecurrence) => {
    setDraft(prev => ({ ...prev, recurrence: newRecurrence }));
  };

  const handleCancel = async () => {
    if (original) {
      const hasChanges =
        draft.status !== original.status ||
        draft.priority !== original.priority ||
        draft.dueDate !== original.dueDate ||
        draft.assigneeUid !== original.assigneeUid ||
        JSON.stringify(draft.labels) !== JSON.stringify(original.labels || []);
      if (hasChanges) {
        await db.updateTask(original.id, {
          status: original.status,
          priority: original.priority,
          dueDate: original.dueDate || null,
          assigneeUid: original.assigneeUid || null,
          labels: original.labels || [],
          updatedByUid: user?.uid || "system"
        });
      }
    }
    history.push(`/tasks/${taskNumber}`);
  };

  const handleNavigate = async (num) => {
    await handleCancel();
    history.push(`/tasks/${num}/edit`);
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!draft.title.trim() || draft.title.length > 150) return;
    try {
      const authorName = user?.displayName || user?.email || "System";
      const base = { authorUid: user?.uid || "system", authorName: authorName, isSystem: true };
      if (draft.status !== original.status) {
        await db.addComment(draft.id, { ...base, type: "state_change", text: `Status changed from "${original.status}" to "${draft.status}" by ${authorName}`, oldValue: original.status, newValue: draft.status });
      }
      if (draft.labels) {
        const userUid = user?.uid || "";
        for (const name of draft.labels) {
          const exists = systemLabels.some(l => l.name.toLowerCase() === name.toLowerCase());
          if (!exists) {
            db.createLabel(name, "#6b7280", userUid).catch(err => console.error("Error creating label in EditTask:", err));
          }
        }
      }
      await db.updateTask(draft.id, {
        title: draft.title.trim(),
        description: draft.description.trim(),
        status: draft.status,
        priority: draft.priority,
        dueDate: draft.dueDate || null,
        assigneeUid: draft.assigneeUid || null,
        labels: draft.labels || [],
        recurrence: draft.recurrence || null,
        updatedByUid: user?.uid || "system"
      });
      history.push(`/tasks/${taskNumber}`);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddComment = async (text) => {
    try {
      await db.addComment(draft.id, {
        authorUid: user?.uid || "system",
        authorName: user?.displayName || user?.email || "Team Member",
        text,
        type: "user",
        isSystem: false
      });
      const updated = await db.getComments(draft.id);
      setComments(updated);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveLabel = async (labelName) => {
    const updated = (draft.labels || []).filter(l => l !== labelName);
    
    try {
      await db.updateTask(draft.id, { labels: updated, updatedByUid: user?.uid || "system" });
      
      setDraft(prev => {
        const next = { ...prev, labels: updated };
        const pref = updated.map(l => `@${l}`).join(" ");
        setInputText(pref ? `${pref} ${prev.title}` : prev.title);
        return next;
      });
      const cms = await db.getComments(draft.id);
      setComments(cms);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenLabels = () => {
    const textarea = document.querySelector(".title-mention-input textarea");
    if (!textarea) return;
    textarea.focus();
    const currentVal = textarea.value;
    const needsSpace = currentVal.length > 0 && !/\s$/.test(currentVal);
    const appendStr = (needsSpace ? " " : "") + "@";
    const startPos = textarea.selectionStart;
    const newVal = currentVal.substring(0, startPos) + appendStr + currentVal.substring(textarea.selectionEnd);
    setInputText(newVal);
    const newPos = startPos + appendStr.length;
    setTimeout(() => textarea.setSelectionRange(newPos, newPos), 0);
  };

  if (err) {
    return (
      <div className="min-h-screen py-12 flex justify-center items-center bg-white">
        <div className="rounded-2xl border border-gray-200 shadow-sm py-12 px-8 text-center max-w-md w-full bg-white">
          <h2 className="text-lg font-bold text-gray-800">Task Not Found</h2>
          <p className="mt-2 text-sm text-gray-500 font-medium">The requested task #{taskNumber} does not exist.</p>
          <Link to="/tasks" className="mt-6 inline-flex justify-center font-bold py-2 px-5 bg-red-500 hover:bg-red-650 text-white rounded-md text-xs select-none shadow-sm transition-all">
            Return to Tasks List
          </Link>
        </div>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="min-h-screen py-12 flex justify-center items-center bg-white text-sm font-semibold text-gray-400">
        Loading task for editing...
      </div>
    );
  }

  const curIdx = allNums.indexOf(Number(taskNumber));
  const prevNum = curIdx > 0 ? allNums[curIdx - 1] : null;
  const nextNum = curIdx !== -1 && curIdx < allNums.length - 1 ? allNums[curIdx + 1] : null;

  return (
    <div className="min-h-screen bg-white py-6 pb-36 px-4 md:px-8 select-text">
      <div className="max-w-5xl mx-auto flex items-center justify-between pb-3 border-b border-gray-150 mb-6 select-none">
        <button onClick={handleCancel} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 font-bold text-sm transition-colors focus:outline-none">
          📥 Inbox
        </button>
        <div className="flex items-center gap-1">
          <button disabled={!prevNum} onClick={() => prevNum && handleNavigate(prevNum)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-md disabled:opacity-30 text-gray-500 hover:text-gray-800 font-bold transition-colors focus:outline-none">∧</button>
          <button disabled={!nextNum} onClick={() => nextNum && handleNavigate(nextNum)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-md disabled:opacity-30 text-gray-500 hover:text-gray-800 font-bold transition-colors focus:outline-none">∨</button>
          <button onClick={handleCancel} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-md text-gray-500 hover:text-gray-800 text-xl transition-colors focus:outline-none font-bold">×</button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8 items-start">
        <div className="space-y-6">
          {draft.category && (!draft.labels || draft.labels.length === 0) && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm font-semibold mb-4">
              Legacy category: {draft.category} — add a label above to replace it
            </div>
          )}

          <div className="flex items-start gap-3">
            <div className="mt-1.5 flex-shrink-0">
              <TaskCircle status={draft.status} priority={draft.priority} onClick={handleToggleStatus} />
            </div>
            <div className="flex-1 min-w-0 space-y-4">
              <div className="title-mention-input">
                <LabelMentionInput value={inputText} onChange={handleInputChange} onSubmit={handleSave} users={users} placeholder="Task title" />
              </div>
              <textarea
                value={draft.description || ""}
                onChange={(e) => setDraft(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description"
                rows={4}
                className="w-full p-3 border border-gray-250 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-all font-medium resize-none min-h-[80px]"
              />
              <div className="flex justify-end gap-3 select-none">
                <button type="button" onClick={handleCancel} className="px-4 py-1.5 border border-gray-200 hover:bg-gray-50 rounded-md text-xs font-bold text-gray-500 transition-all cursor-pointer">Cancel</button>
                <button type="button" onClick={handleSave} className="px-4 py-1.5 bg-red-500 hover:bg-red-650 text-white rounded-md text-xs font-bold shadow-sm transition-all cursor-pointer">Save</button>
              </div>

              {/* Mobile Stacked Metadata List */}
              <div className="block lg:hidden pt-4 border-t border-gray-100 space-y-0 text-left">
                {/* 4. Project/Inbox */}
                <div className="flex items-center justify-between py-3 border-b border-gray-100 text-sm">
                  <div className="flex items-center gap-2 text-gray-500 font-medium">
                    <span className="text-gray-400">📥</span>
                    <span>Project</span>
                  </div>
                  <span className="text-gray-700 font-semibold">Inbox</span>
                </div>

                {/* 5. Date */}
                <div className="flex items-center justify-between py-3 border-b border-gray-100 text-sm">
                  <div className="flex items-center gap-2 text-gray-500 font-medium">
                    <span className="text-gray-400">📅</span>
                    <span>Date</span>
                  </div>
                  <DatePicker value={draft.dueDate} onChange={handleDateChange} />
                </div>

                {/* Recurrence */}
                <div className="flex items-center justify-between py-3 border-b border-gray-100 text-sm">
                  <div className="flex items-center gap-2 text-gray-500 font-medium">
                    <span className="text-gray-400">🔄</span>
                    <span>Repeat</span>
                  </div>
                  <RecurrencePicker value={draft.recurrence || null} onChange={handleRecurrenceChange} dropdownAlign="right" />
                </div>

                {/* 6. Deadline (decorative) */}
                <div className="flex items-center justify-between py-3 border-b border-gray-100 text-sm opacity-60">
                  <div className="flex items-center gap-2 text-gray-500 font-medium">
                    <span className="text-gray-400">⌚</span>
                    <span>Deadline</span>
                  </div>
                  <span className="text-gray-400 font-semibold">⊕</span>
                </div>

                {/* 7. Priority */}
                <div className="flex items-center justify-between py-3 border-b border-gray-100 text-sm">
                  <div className="flex items-center gap-2 text-gray-500 font-medium">
                    <span className="text-gray-400">🏴</span>
                    <span>Priority</span>
                  </div>
                  <PriorityPicker value={draft.priority} onChange={handlePriorityChange} />
                </div>

                {/* 8. Labels */}
                <div className="flex items-start justify-between py-3 border-b border-gray-100 text-sm">
                  <div className="flex items-center gap-2 text-gray-500 font-medium mt-0.5">
                    <span className="text-gray-400">♡</span>
                    <span>Labels</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 justify-end max-w-[70%]">
                    {(draft.labels || []).map((lName) => (
                      <LabelPill key={lName} name={lName} onRemove={() => handleRemoveLabel(lName)} />
                    ))}
                    <button
                      type="button"
                      onClick={handleOpenLabels}
                      className="text-red-500 hover:text-red-650 font-semibold text-xs transition-colors"
                    >
                      + Add
                    </button>
                  </div>
                </div>

                {/* 9. Reminders (decorative) */}
                <div className="flex items-center justify-between py-3 border-b border-gray-100 text-sm opacity-60">
                  <div className="flex items-center gap-2 text-gray-500 font-medium">
                    <span className="text-gray-400">🔔</span>
                    <span>Add reminders</span>
                  </div>
                  <span className="text-gray-400 font-semibold">⊕</span>
                </div>

                {/* 10. Location (decorative) */}
                <div className="flex items-center justify-between py-3 border-b border-gray-100 text-sm opacity-60">
                  <div className="flex items-center gap-2 text-gray-500 font-medium">
                    <span className="text-gray-400">📍</span>
                    <span>Add location</span>
                  </div>
                  <span className="text-gray-400 font-semibold">⊕</span>
                </div>

                {/* 11. Add sub-task (decorative) */}
                <div className="py-3 border-b border-gray-100 text-sm opacity-60">
                  <button type="button" className="text-red-500 font-semibold text-xs flex items-center gap-1.5 focus:outline-none">
                    <span className="text-base font-bold">+</span> Add sub-task
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden lg:block border-t border-gray-100 pt-4">
            <div className="overflow-x-auto scrollbar-none -mx-3 px-3 [&>div]:flex-nowrap [&>div]:overflow-x-auto [&>div]:scrollbar-none [&>div]:py-1">
              <TaskToolbar
                dueDate={draft.dueDate}
                onChangeDate={handleDateChange}
                priority={draft.priority}
                onChangePriority={handlePriorityChange}
                assigneeUid={draft.assigneeUid}
                onChangeAssignee={(u) => {
                  const oldN = getUserName(draft.assigneeUid);
                  const newN = getUserName(u);
                  changeField("assigneeUid", u || null, { type: "assignment_change", text: `Assignee changed from "${oldN}" to "${newN}"`, oldValue: oldN, newValue: newN });
                }}
                users={users}
                onOpenLabels={handleOpenLabels}
              />
            </div>
          </div>

          <div className="hidden lg:block pl-1">
            <button type="button" className="text-gray-400 hover:text-gray-650 font-bold text-xs transition-colors flex items-center gap-1 focus:outline-none select-none">
              <span className="text-sm font-semibold">+</span> Add sub-task
            </button>
          </div>

          <hr className="border-gray-100" />
          <CommentThread comments={comments} user={user} onAddComment={handleAddComment} />
        </div>

        <div className="hidden lg:block lg:col-span-1 select-none">
          <TaskRightPanel
            task={draft}
            users={users}
            labels={systemLabels}
            user={user}
            onFieldSaved={(field, val) => {
              setDraft(prev => {
                const next = { ...prev, [field === "assignee" ? "assigneeUid" : field]: val };
                if (field === "labels") {
                  const pref = (val || []).map(l => `@${l}`).join(" ");
                  setInputText(pref ? `${pref} ${prev.title}` : prev.title);
                }
                return next;
              });
            }}
          />
        </div>
      </div>
    </div>
  );
}
